import { InlineEditableField } from 'app/components/Common/EditPage/InlineEditableField';
import React, { useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { editableFields } from '../../editUtils';
import { pageAttributesActions } from '../store/placementPageAttributes.redux';
import { selectEditStatus, selectPageAttributes } from '../store/placementPageAttributes.selector';
import { ActionAccessorHook } from './ActionAccessorHook';
import { Popper } from '@mui/material';
import { TypeAhead } from '@AMIEWEB/Common';
import { orderBy } from 'lodash';
import { ITypeAheadOption } from 'app/models/Orders/OrderDetails';
import { useTranslation } from 'react-i18next';
import { lookupOptionSelector } from 'store/redux-store/lookup/lookup.selectors';
import { selectEditableData } from 'store/redux-store/placement-details/selectors';

export const PlacementShift = ({ id }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { control } = useFormContext();
  const { placementShift: status } = useSelector(selectEditStatus);
  const { editOrderStack } = useSelector(selectPageAttributes);
  const [shiftOptions, setShiftOptions] = useState<ITypeAheadOption[]>([]);
  const { shiftListOptions: masterOpts } = useSelector(lookupOptionSelector);
  const { placementShift: defaultValue } = useSelector(selectEditableData);
  const undoChecker = () => !editOrderStack.some(key => key === 'placementShift');

  /** Code: useWatch has been used to retain value while avoidParentForm is triggered in Placement screen wrapper */
  const placementShiftValue = useWatch<ITypeAheadOption>({ name: 'placementShift', control });

  useEffect(() => {
    if (masterOpts?.length > 0) {
      const newOptions = (masterOpts || []).map(opt => ({
        object: { id: opt.value, name: opt.name, value: opt.value },
        label: opt.name,
      }));
      setShiftOptions(orderBy(newOptions, 'object.id', 'asc'));
    }
  }, [masterOpts]);

  return (
    <Controller
      control={control}
      name="placementShift"
      render={({ field: { onChange, ...rest } }) => (
        <>
          {/** Component is used to access inaccessible methods from react-hook-form Controller
           * to invoke actions - say undo action */}
          <ActionAccessorHook
            actionChecker={undoChecker}
            accessAction={() => onChange(defaultValue)}
            hookDependancies={[editOrderStack]}
          />
          <InlineEditableField
            {...status}
            {...rest}
            id={id}
            inputProps={{
              ...rest,
              variant: 'outlined',
              size: 'small',
              options: shiftOptions ?? [],
              autoFocus: true,
              enableCustomScroll: true,
              removeCloseIcon: true,
              hasAsyncOptions: true,
              invokeInitialAsyncData: true,
              onChangeReflection: loc => {
                if (loc) {
                  onChange(loc);
                }
              },
              placeholder: placementShiftValue ? placementShiftValue?.label : 'Select',
              popperComponent: PopperComponent,
            }}
            onChange={newValue => {
              onChange(newValue);
              dispatch(pageAttributesActions.pushToEditOrderStack(editableFields.placementShift));
            }}
            Control={TypeAhead}
            label={placementShiftValue?.label ?? defaultValue?.label}
            value={placementShiftValue ?? defaultValue}
            tooltipText={status?.disabled ? t('placement.profile.edit.disableText') : ''}
            alignStart
            setCurrentFocus={state => dispatch(pageAttributesActions.setCurrentFocus(state !== null ? id : null))}
            openOnChange={false}
          />
        </>
      )}
    />
  );
};

const PopperComponent = props => (
  <Popper {...props} style={{ width: 'fit-content', minWidth: 150 }} placement="bottom-start" />
);
